import { ChevronDown, ChevronLeft, Lock } from 'lucide-react';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { useOptionalBilling } from '../../context/BillingContext';
import { planLock } from '../billing/planLock';
import { isAdminSession } from '../admin/utils/adminPreview';
import { NAV_GROUPS, activePathFor, labelFor, workspaceBadgeLabel, type NavLink } from './navigation';
import styles from './AppSidebar.module.css';

/**
 * The one sidebar, rendered from the one menu definition (./navigation).
 *
 * Every shell behind sign-in uses this, so the menu cannot differ by screen —
 * which it did, in four different ways. A shell passes its own header through
 * ``badge`` and its own footer through ``footer``; it does not get to choose
 * the links.
 *
 * Plan-gated entries are shown to everyone with a lock and the plan that
 * includes them, rather than hidden: a menu that silently omits what the
 * customer is not paying for cannot sell the upgrade (SRS #140).
 */
interface Props {
  /** e.g. "Business dashboard" — the shell's own label for this workspace. */
  badge?: string;
  badgeSubtitle?: string;
  /** Force the collapsed state; omitted, the sidebar remembers the viewer's own. */
  collapsed?: boolean;
  /** Sign out, theme toggle, the account card — whatever the shell puts at the bottom. */
  footer?: React.ReactNode;
  /**
   * Render only the grouped links, with no <aside> around them.
   *
   * The main app's sidebar is not only a menu — it also carries the chat list —
   * so it keeps its own chrome and takes just the links. The point of the
   * shared definition is that the entries match everywhere, not that every
   * shell looks identical.
   */
  navOnly?: boolean;
}

const COLLAPSE_KEY = 'policyora_sidebar_collapsed';
const GROUPS_KEY = 'policyora_sidebar_groups';

/** Which menu groups the viewer opened or closed. A group left alone follows the page they are on. */
function rememberedGroups(): Record<string, boolean> {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(GROUPS_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function rememberedCollapse(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;   // private windows and blocked storage must not break the menu
  }
}

export const AppSidebar: React.FC<Props> = ({ badge, badgeSubtitle, collapsed: forced, footer, navOnly = false }) => {
  // Collapsing belongs here rather than in each shell: it was per-shell state,
  // so the sidebar re-expanded every time the customer moved between screens.
  const [ownCollapsed, setOwnCollapsed] = React.useState(rememberedCollapse);
  // Groups are collapsible so the whole menu fits a laptop-height window. Only the group holding the current
  // page opens by default, and the viewer's own choice is kept; a folded group still lists its page when the sidebar is icons only.
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(rememberedGroups);
  const collapsed = forced ?? ownCollapsed;
  const toggle = () => {
    const next = !collapsed;
    setOwnCollapsed(next);
    try { window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* not worth failing over */ }
  };

  // Publish the width so a shell laying itself out in a grid can reserve the
  // right track. The dashboard reserved 170px for a 252px sidebar, so its
  // content started underneath the menu and covered half the labels.
  React.useEffect(() => {
    const px = collapsed ? '76px' : '252px';
    document.documentElement.style.setProperty('--app-sidebar-width', px);
  }, [collapsed]);
  // Tolerates a missing BillingProvider rather than throwing: the sidebar
  // renders in places that do not mount one (tests, the admin preview), and a
  // menu that takes the page down with it is worse than one that shows every
  // entry unlocked. Locking fails open for the same reason — hiding an entry
  // we cannot price is how a customer loses a feature they are paying for.
  const billing = useOptionalBilling();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = activePathFor(pathname);
  const adminPreview = isAdminSession();
  const segment = billing?.entitlements?.segment ?? null;
  const { user } = useAuth();
  // The logo lived in the main app's own chrome, so every shell that used this
  // sidebar as its whole aside — the dashboard, startup, government and
  // enterprise — had no logo at all. White-labelling is honoured here the same
  // way it is there (SRS #38).
  const { brand, logo: brandLogo } = useBranding();
  // The shell may name the workspace itself; otherwise the plan does.
  const badgeText = badge ?? workspaceBadgeLabel(billing?.subscription?.plan_tier, user?.organization_type);
  const subtitle = badgeSubtitle ?? user?.organization_name ?? undefined;

  const renderLink = (link: NavLink) => {
    const lock = link.entry && !adminPreview && billing
      ? planLock(billing, link.entry)
      : { locked: false, title: '' };
    const isActive = active === link.path;
    const label = labelFor(link, segment);

    // A locked entry goes to pricing rather than to a page that will refuse it.
    if (lock.locked) {
      return (
        <button
          key={link.path}
          type="button"
          className={`${styles.link} ${styles.locked}`}
          title={lock.title}
          aria-label={`${label} — ${lock.title}`}
          onClick={() => navigate('/pricing', { state: { changeOnly: true, reason: `${label} is not in your plan` } })}
        >
          <span className={styles.icon}>{link.icon}</span>
          <span className={styles.label}>{label}</span>
          <Lock size={13} className={styles.lockIcon} aria-hidden />
        </button>
      );
    }

    return (
      <Link
        key={link.path}
        to={link.path}
        className={`${styles.link} ${isActive ? styles.active : ''}`}
        aria-current={isActive ? 'page' : undefined}
        title={label}
      >
        <span className={styles.icon}>{link.icon}</span>
        <span className={styles.label}>{label}</span>
      </Link>
    );
  };

  // A page outside the menu (a shell's own screen) opens the first group, so the menu is never a blank column.
  const activeGroup = NAV_GROUPS.find((g) => g.links.some((l) => l.path === active))?.title ?? NAV_GROUPS[0]?.title;
  const isOpen = (title: string) => openGroups[title] ?? title === activeGroup;
  const toggleGroup = (title: string) => {
    const next = { ...openGroups, [title]: !isOpen(title) };
    setOpenGroups(next);
    try { window.localStorage.setItem(GROUPS_KEY, JSON.stringify(next)); } catch { /* not worth failing over */ }
  };
  const groups = (
    <>
      {NAV_GROUPS.map((group) => {
        const links = group.links.filter((l) => !(l.customerOnly && adminPreview));
        if (!links.length) return null;
        const open = collapsed || isOpen(group.title);
        return (
          <nav key={group.title} aria-label={group.title}>
            {collapsed ? (
              <div className={styles.groupTitle}>{group.title}</div>
            ) : (
              <button
                type="button"
                className={styles.groupToggle}
                aria-expanded={open}
                onClick={() => toggleGroup(group.title)}
              >
                <span>{group.title}</span>
                <span className={styles.groupCount}>{links.length}</span>
                <ChevronDown size={13} className={open ? styles.groupChevronOpen : styles.groupChevron} aria-hidden />
              </button>
            )}
            {open && links.map(renderLink)}
          </nav>
        );
      })}
    </>
  );

  if (navOnly) return groups;

  return (
    <aside className={`${styles.aside} ${collapsed ? styles.collapsed : ''}`} aria-label="Main">
      <button
        type="button"
        className={styles.collapseBtn}
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand the menu' : 'Collapse the menu'}
        title={collapsed ? 'Expand the menu' : 'Collapse the menu'}
      >
        <ChevronLeft size={16} className={collapsed ? styles.collapseIconFlipped : ''} aria-hidden />
      </button>
      {!navOnly && (
        <Link to="/dashboard" className={styles.brand} aria-label="Policyora home">
          <img
            src={brandLogo || '/Policy Ora Final Logo.png'}
            alt={brandLogo ? (brand?.brand_name || 'Workspace logo') : 'Policyora'}
            className={styles.brandLogo}
            data-testid="sidebar-logo"
          />
        </Link>
      )}
      {badgeText && <span className={styles.badge}>{badgeText}</span>}
      {subtitle && <div className={styles.badgeSub}>{subtitle}</div>}

      {groups}

      {footer && <><div className={styles.spacer} />{footer}</>}
    </aside>
  );
};

export default AppSidebar;
